import { supabase } from '../client';

export type PdfDocKind =
  | 'invoice_order'
  | 'payment_receipt'
  | 'proposal'
  | 'account_summary'
  | 'price_list';

export type PdfTemplateCode = 'template_1' | 'template_2' | 'template_3';
export type PdfOrientation = 'portrait' | 'landscape';
export type PdfThemeMode = 'default' | 'custom';

export interface StorePdfTemplate {
  id: string;
  store_id: string;
  org_id: string;
  doc_kind: PdfDocKind;
  template_code: PdfTemplateCode;
  name: string;
  orientation: PdfOrientation;
  qr_enabled: boolean;
  qr_target_url: string | null;
  use_default_logo: boolean;
  logo_url: string | null;
  company_name: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_address: string | null;
  payment_make_payable_to: string | null;
  payment_instructions: string | null;
  theme_mode: PdfThemeMode;
  theme_primary_hex: string | null;
  theme_secondary_hex: string | null;
  theme_accent_hex: string | null;
  theme_text_hex: string | null;
  theme_background_hex: string | null;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

export interface UpsertStorePdfTemplateInput {
  storeId: string;
  docKind: PdfDocKind;
  templateCode: PdfTemplateCode;
  name?: string | null;
  orientation?: PdfOrientation | null;
  qrEnabled?: boolean | null;
  qrTargetUrl?: string | null;
  useDefaultLogo?: boolean | null;
  logoUrl?: string | null;
  companyName?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  contactAddress?: string | null;
  paymentMakePayableTo?: string | null;
  paymentInstructions?: string | null;
  themeMode?: PdfThemeMode | null;
  themePrimaryHex?: string | null;
  themeSecondaryHex?: string | null;
  themeAccentHex?: string | null;
  themeTextHex?: string | null;
  themeBackgroundHex?: string | null;
}

export async function listStorePdfTemplates(
  storeId: string,
): Promise<StorePdfTemplate[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_list_store_pdf_templates', { p_store_id: storeId });
  if (error) throw error;
  return (data ?? []) as StorePdfTemplate[];
}

export async function upsertStorePdfTemplate(
  input: UpsertStorePdfTemplateInput,
): Promise<StorePdfTemplate> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_upsert_store_pdf_template', {
      p_store_id: input.storeId,
      p_doc_kind: input.docKind,
      p_template_code: input.templateCode,
      p_name: input.name ?? null,
      p_orientation: input.orientation ?? null,
      p_qr_enabled: input.qrEnabled ?? null,
      p_qr_target_url: input.qrTargetUrl ?? null,
      p_use_default_logo: input.useDefaultLogo ?? null,
      p_logo_url: input.logoUrl ?? null,
      p_company_name: input.companyName ?? null,
      p_contact_name: input.contactName ?? null,
      p_contact_email: input.contactEmail ?? null,
      p_contact_phone: input.contactPhone ?? null,
      p_contact_address: input.contactAddress ?? null,
      p_payment_make_payable_to: input.paymentMakePayableTo ?? null,
      p_payment_instructions: input.paymentInstructions ?? null,
      p_theme_mode: input.themeMode ?? null,
      p_theme_primary_hex: input.themePrimaryHex ?? null,
      p_theme_secondary_hex: input.themeSecondaryHex ?? null,
      p_theme_accent_hex: input.themeAccentHex ?? null,
      p_theme_text_hex: input.themeTextHex ?? null,
      p_theme_background_hex: input.themeBackgroundHex ?? null,
    });
  if (error) throw error;
  return data as StorePdfTemplate;
}

export async function resetStorePdfTemplate(
  storeId: string,
  docKind: PdfDocKind,
  templateCode: PdfTemplateCode,
): Promise<StorePdfTemplate> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_reset_store_pdf_template', {
      p_store_id: storeId,
      p_doc_kind: docKind,
      p_template_code: templateCode,
    });
  if (error) throw error;
  return data as StorePdfTemplate;
}
